<template>
  <nav :aria-label="ariaLabel">
    <ul class="pagination" :class="paginationClass">
      <!-- Previous button -->
      <li class="page-item" :class="{ disabled: currentPage === 1 }">
        <button class="page-link" :aria-label="previousButtonLabel" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
          <span aria-hidden="true">{{ previousButtonText }}</span>
        </button>
      </li>

      <!-- First page + ellipsis -->
      <template v-for="(page, index) in visiblePages">
        <li v-if="page === 'ellipsis'" class="page-item disabled" :key="index">
          <span class="page-link">…</span>
        </li>

        <!-- Page numbers -->
        <li v-else class="page-item" :class="{ active: currentPage === page }">
          <button class="page-link" @click="goToPage(page)" :aria-current="currentPage === page ? 'page' : undefined">
            {{ page }}
          </button>
        </li>
      </template>

      <!-- Next button -->
      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
        <button class="page-link" :aria-label="nextButtonLabel" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
          <span aria-hidden="true">{{ nextButtonText }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'CustomPagination',

  props: {
    // Current active page
    currentPage: {
      type: Number,
      required: true,
    },

    // Number of items per page
    perPage: {
      type: Number,
      required: true,
    },

    // Total number of row
    totalRow: {
      type: Number,
      required: true,
    },

    // Maximum number of page buttons to show (excluding prev/next)
    maxVisibleButtons: {
      type: Number,
      default: 5,
    },

    // Always show pagination even when there's only one page
    alwaysShow: {
      type: Boolean,
      default: true,
    },

    // Customize button text
    previousButtonText: {
      type: String,
      default: 'Previous',
    },

    nextButtonText: {
      type: String,
      default: 'Next',
    },

    // Additional CSS classes for the pagination container
    paginationClass: {
      type: [String, Object, Array],
      default: '',
    },

    // ARIA label for navigation
    ariaLabel: {
      type: String,
      default: 'Pagination navigation',
    },

    // Custom ARIA labels for buttons
    previousButtonLabel: {
      type: String,
      default: 'Go to previous page',
    },

    nextButtonLabel: {
      type: String,
      default: 'Go to next page',
    },
  },

  emits: ['page-change'],

  computed: {
    totalPages() {
      return Math.ceil(this.totalRow / this.perPage);
    },

    /**
     * Calculate which page numbers should be visible
     */
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const maxButtons = this.maxVisibleButtons;

      // Always show pagination, even with one page
      if (total <= 1) {
        pages.push(1);
        return pages;
      }

      if (total <= maxButtons) {
        // Show all pages
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Complex pagination with ellipsis
        const halfButtons = Math.floor(maxButtons / 2);
        let start = Math.max(current - halfButtons, 1);
        let end = Math.min(start + maxButtons - 1, total);

        // Adjust if we're at the end
        if (end - start + 1 < maxButtons) {
          start = Math.max(end - maxButtons + 1, 1);
        }

        // Add first page with ellipsis if needed
        if (start > 1) {
          pages.push(1);
          if (start > 2) {
            pages.push('ellipsis');
          }
        }

        // Add page numbers
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        // Add last page with ellipsis if needed
        if (end < total) {
          if (end < total - 1) {
            pages.push('ellipsis');
          }
          pages.push(total);
        }
      }

      return pages;
    },
  },

  methods: {
    /**
     * Navigate to a specific page
     */
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit('page-change', page);
      }
    },
  },
};
</script>

<style scoped>
/* Optional custom styles - Bootstrap handles most of it */
.pagination {
  margin-bottom: 0;
}

.page-link {
  cursor: pointer;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
  pointer-events: auto;
}

/* Optional: Add spacing between pagination controls */
.pagination .page-item + .page-item {
  margin-left: 2px;
}
</style>
